/**
 * Блок «Административное деление» районной вкладки (US-11 FR-2).
 *
 * Три счётчика состава района: сельские округа · сёла · города районного
 * значения. Значения выводятся как есть, без пересчёта на стороне экрана
 * (FR-2.2) — источник объявит их отдельной операцией (US-12 FR-5).
 *
 * Заголовок блока — обычный текст: страницы административно-территориального
 * устройства района в системе нет, перехода из заголовка не выполняется
 * (FR-2.3, решение 4 от 03.09).
 *
 * Нет значения по счётчику → счётчик не показывается вовсе, остальные
 * равномерно занимают ширину (FR-2.4): «—» и «0» читались бы как данные.
 * Нет ни одного → состояние «нет данных» по блоку.
 *
 * Для города областного значения блок не выводится вовсе (FR-2.5) — это
 * решает страница, здесь такого состояния нет.
 */
import { useTranslation } from 'react-i18next';

import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '../../../../components/ui/card';

export interface DistrictDivisionCounts {
  /** Сельские округа, ед. */
  okrugs: number | null;
  /** Сёла, ед. */
  villages: number | null;
  /** Города районного значения, ед. */
  cities: number | null;
}

interface Props {
  /** null — источник не ответил / операции ещё нет → «нет данных». */
  counts: DistrictDivisionCounts | null;
  /** Отчётная дата блока — своя, из данных блока (FR-7.2). */
  periodLabel?: string | null;
}

const COUNTER_KEYS = ['okrugs', 'villages', 'cities'] as const;

const fmt = (v: number): string => Intl.NumberFormat('ru-RU').format(v);

export const DistrictDivision = ({ counts, periodLabel }: Props) => {
  const { t } = useTranslation();

  const shown = COUNTER_KEYS.flatMap((key) => {
    const value = counts?.[key] ?? null;
    return value === null ? [] : [{ key, value }];
  });

  return (
    <Card className="h-full shadow-card">
      <CardHeader className="flex-row items-baseline justify-between space-y-0">
        {/* Обычный текст, не ссылка (FR-2.3). */}
        <CardTitle>{t('digitalPassport.districtDivision.title')}</CardTitle>
        {periodLabel != null && (
          <span
            className="text-sm text-text-muted"
            data-testid="district-division-period"
          >
            {periodLabel}
          </span>
        )}
      </CardHeader>
      <CardContent>
        <section data-testid="block-district-division">
          {shown.length === 0 ? (
            <p
              data-testid="block-placeholder"
              className="text-sm lowercase text-text-muted"
            >
              {t('common.noData')}
            </p>
          ) : (
            <ul
              className="grid gap-3.5"
              style={{
                gridTemplateColumns: `repeat(${shown.length}, minmax(0, 1fr))`,
              }}
              data-testid="district-division-counters"
            >
              {shown.map(({ key, value }) => (
                <li
                  key={key}
                  data-testid={`district-division-${key}`}
                  className="rounded-2xl border border-surface-border bg-gradient-to-b from-surface to-surface-page p-5 shadow-card"
                >
                  <span className="block text-2xl font-extrabold tabular-nums text-brand-600">
                    {fmt(value)}
                  </span>
                  <span className="mt-1 block text-sm leading-snug text-text-muted">
                    {t(`digitalPassport.districtDivision.${key}`)}
                  </span>
                  <span className="mt-0.5 block text-xs text-text-faint">
                    {t('digitalPassport.districtDivision.unit')}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </section>
      </CardContent>
    </Card>
  );
};
